import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminBookingDetails = () => {
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // UI state for expandable content
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedFields, setExpandedFields] = useState(new Map());

  // Utility functions for text handling
  const truncateText = (text, maxLength = 25) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const toggleFieldExpansion = (bookingId, fieldName) => {
    const key = `${bookingId}-${fieldName}`;
    setExpandedFields(prev => {
      const newMap = new Map(prev);
      newMap.set(key, !newMap.get(key));
      return newMap;
    });
  };

  const isFieldExpanded = (bookingId, fieldName) => {
    const key = `${bookingId}-${fieldName}`;
    return expandedFields.get(key) || false;
  };

  const toggleRowExpansion = (bookingId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookingId)) {
        newSet.delete(bookingId);
      } else {
        newSet.add(bookingId);
      }
      return newSet;
    });
  };

  const isRowExpanded = (bookingId) => {
    return expandedRows.has(bookingId);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [bookingDetails, selectedStatuses, searchTerm]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const statuses = [
        { value: 'BOOKED', label: 'Booked' },
        { value: 'HOLD', label: 'Hold' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'CANCELLED', label: 'Cancelled' },
        { value: 'NOT_SET', label: 'Not Set' },
        { value: 'REJECTED', label: 'Rejected' }
      ];
      setAvailableStatuses(statuses);
      
      const response = await adminApiService.getAllBookingDetails();
      if (response.success) {
        setBookingDetails(response.data.bookings || []);
      } else {
        toast.error(response.message || 'Failed to load booking details');
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load initial data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bookingDetails];

    // Apply status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(booking => 
        selectedStatuses.includes(booking.status)
      );
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking =>
        (booking.bookingId && booking.bookingId.toLowerCase().includes(term)) ||
        (booking.hotelName && booking.hotelName.toLowerCase().includes(term)) ||
        (booking.hotelConfirmationNo && booking.hotelConfirmationNo.toLowerCase().includes(term)) ||
        (booking.confirmationNo && booking.confirmationNo.toLowerCase().includes(term)) ||
        (booking.address && booking.address.toLowerCase().includes(term)) ||
        (booking.city && booking.city.toLowerCase().includes(term)) ||
        (booking.agentId && booking.agentId.toLowerCase().includes(term))
      );
    }
    
    setFilteredBookings(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleStatusChange = (statusValue) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusValue)) {
        return prev.filter(status => status !== statusValue);
      } else {
        return [...prev, statusValue];
      }
    });
  };

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSearchTerm('');
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'BOOKED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'HOLD':
      case 'IN_PROGRESS':
      case 'PENDING':
      case 'NOT_SET':
      case 'REJECTED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return `₹${parseFloat(price).toLocaleString('en-IN')}`;
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = filteredBookings.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <i className="fas fa-calendar-check text-blue-600 mr-3"></i>
                Booking Management
              </h1>
              <p className="mt-2 text-gray-600">Manage and track all hotel bookings across the platform</p>
            </div>
            <div className="hidden sm:flex items-center space-x-4">
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100">
                <span className="text-sm text-gray-500">Total Bookings</span>
                <p className="text-2xl font-bold text-blue-600">{bookingDetails.length}</p>
              </div>
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100">
                <span className="text-sm text-gray-500">Filtered Results</span>
                <p className="text-2xl font-bold text-green-600">{filteredBookings.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Bookings
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search by booking ID, agent, hotel, city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            </div>

            {/* Status Filters */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <div className="flex flex-wrap gap-3">
                {availableStatuses.map((status) => (
                  <label key={status.value} className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.value)}
                      onChange={() => handleStatusChange(status.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <span className="text-sm font-medium text-gray-700">{status.label}</span>
                  </label>
                ))}
              </div>
              {(selectedStatuses.length > 0 || searchTerm.trim()) && (
                <button
                  onClick={clearFilters}
                  className="mt-3 bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors duration-200"
                >
                  <i className="fas fa-times mr-2"></i>
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Booking Details Cards */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold flex items-center">
                  <i className="fas fa-list mr-2"></i>
                  All Bookings
                </h3>
                {currentBookings.length > 0 && (
                  <p className="text-sm opacity-90 mt-1">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''}
                    {bookingDetails.length !== filteredBookings.length && ` (filtered from ${bookingDetails.length} total)`}
                  </p>
                )}
              </div>
              
              {/* Items per page selector */}
              {currentBookings.length > 0 && (
                <div className="flex items-center space-x-2 mt-3 sm:mt-0">
                  <span className="text-sm">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                    className="bg-white text-gray-900 rounded px-3 py-1 text-sm border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm">per page</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6">
            {currentBookings?.length > 0 ? (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Booking Details
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Hotel Information
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Dates
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentBookings.map((booking, index) => (
                          <tr key={booking.id || index} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-6 py-4">
                              <div className="space-y-2">
                                <div>
                                  <button
                                    onClick={() => navigate(`/admin/dashboard/booking-details/${booking.bookingId}`)}
                                    className="font-semibold text-blue-600 hover:text-blue-800 transition-colors duration-200 cursor-pointer"
                                  >
                                    {booking.bookingId || '-'}
                                  </button>
                                </div>
                                {booking.agentId && (
                                  <div className="text-sm text-gray-600">
                                    Agent ID: <span className="font-medium text-gray-800">{booking.agentId}</span>
                                  </div>
                                )}
                                {booking.hotelConfirmationNo && (
                                  <div className="text-sm text-gray-600">
                                    Hotel Conf: {booking.hotelConfirmationNo}
                                  </div>
                                )}
                                {booking.confirmationNo && (
                                  <div className="text-sm text-gray-600">
                                    Booking Conf: {booking.confirmationNo}
                                  </div>
                                )}
                                
                                {/* Price Drop Badge */}
                                {booking.isPriceDrop && (
                                  <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 border border-green-200 rounded-full text-xs font-medium">
                                    <i className="fas fa-arrow-down mr-1"></i>
                                    Price Drop: ₹{booking.newPrice} [{booking.providerName}]
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-2">
                                <div className="font-medium text-gray-900">
                                  {isFieldExpanded(booking.bookingId, 'hotelName') || !booking.hotelName || booking.hotelName.length <= 30
                                    ? booking.hotelName || '-'
                                    : (
                                      <div>
                                        {truncateText(booking.hotelName, 30)}
                                        <button
                                          onClick={() => toggleFieldExpansion(booking.bookingId, 'hotelName')}
                                          className="ml-2 text-blue-600 hover:text-blue-800 text-xs font-medium"
                                        >
                                          See More
                                        </button>
                                      </div>
                                    )
                                  }
                                  {isFieldExpanded(booking.bookingId, 'hotelName') && booking.hotelName && booking.hotelName.length > 30 && (
                                    <button
                                      onClick={() => toggleFieldExpansion(booking.bookingId, 'hotelName')}
                                      className="ml-2 text-blue-600 hover:text-blue-800 text-xs font-medium"
                                    >
                                      See Less
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                  <i className="fas fa-map-marker-alt text-red-500 mr-2"></i>
                                  {isFieldExpanded(booking.bookingId, 'location') || (!booking.address && !booking.city) || (booking.address || booking.city || '').length <= 25
                                    ? (booking.address || booking.city || '-')
                                    : (
                                      <div>
                                        {truncateText(booking.address || booking.city, 25)}
                                        <button
                                          onClick={() => toggleFieldExpansion(booking.bookingId, 'location')}
                                          className="ml-2 text-blue-600 hover:text-blue-800 text-xs font-medium"
                                        >
                                          See More
                                        </button>
                                      </div>
                                    )
                                  }
                                  {isFieldExpanded(booking.bookingId, 'location') && (booking.address || booking.city) && (booking.address || booking.city).length > 25 && (
                                    <button
                                      onClick={() => toggleFieldExpansion(booking.bookingId, 'location')}
                                      className="ml-2 text-blue-600 hover:text-blue-800 text-xs font-medium"
                                    >
                                      See Less
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center">
                                  <i className="fas fa-calendar-plus text-green-600 mr-2"></i>
                                  <span className="font-medium">Check-in:</span>
                                  <span className="ml-2">{formatDate(booking.checkInDate)}</span>
                                </div>
                                <div className="flex items-center">
                                  <i className="fas fa-calendar-minus text-red-600 mr-2"></i>
                                  <span className="font-medium">Check-out:</span>
                                  <span className="ml-2">{formatDate(booking.checkOutDate)}</span>
                                </div>
                                <div className="flex items-center text-gray-600">
                                  <i className="fas fa-calendar text-gray-400 mr-2"></i>
                                  <span>Booked:</span>
                                  <span className="ml-2">{formatDate(booking.bookingDate)}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm font-semibold text-green-600">
                                {formatPrice(booking.totalPrice)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(booking.status)}`}>
                                {booking.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => toggleRowExpansion(booking.bookingId)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
                              >
                                <i className={`fas ${isRowExpanded(booking.bookingId) ? 'fa-chevron-up' : 'fa-chevron-down'} mr-1`}></i>
                                {isRowExpanded(booking.bookingId) ? 'Less' : 'More'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                  {currentBookings.map((booking, index) => (
                    <div key={booking.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <button
                            onClick={() => navigate(`/admin/dashboard/booking-details/${booking.bookingId}`)}
                            className="font-semibold text-blue-600 hover:text-blue-800 text-lg cursor-pointer"
                          >
                            {booking.bookingId || '-'}
                          </button>
                          <div className="mt-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(booking.status)}`}>
                              {booking.status || 'N/A'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleRowExpansion(booking.bookingId)}
                          className="text-blue-600 hover:text-blue-800 p-2"
                        >
                          <i className={`fas ${isRowExpanded(booking.bookingId) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">
                            {isFieldExpanded(booking.bookingId, 'hotelName') || !booking.hotelName || booking.hotelName.length <= 40
                              ? booking.hotelName || '-'
                              : (
                                <div>
                                  {truncateText(booking.hotelName, 40)}
                                  <button
                                    onClick={() => toggleFieldExpansion(booking.bookingId, 'hotelName')}
                                    className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                  >
                                    See More
                                  </button>
                                </div>
                              )
                            }
                            {isFieldExpanded(booking.bookingId, 'hotelName') && booking.hotelName && booking.hotelName.length > 40 && (
                              <button
                                onClick={() => toggleFieldExpansion(booking.bookingId, 'hotelName')}
                                className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                See Less
                              </button>
                            )}
                          </h4>
                          <div className="flex items-center text-sm text-gray-600">
                            <i className="fas fa-map-marker-alt text-red-500 mr-2"></i>
                            {isFieldExpanded(booking.bookingId, 'location') || (!booking.address && !booking.city) || (booking.address || booking.city || '').length <= 30
                              ? (booking.address || booking.city || '-')
                              : (
                                <div>
                                  {truncateText(booking.address || booking.city, 30)}
                                  <button
                                    onClick={() => toggleFieldExpansion(booking.bookingId, 'location')}
                                    className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                  >
                                    See More
                                  </button>
                                </div>
                              )
                            }
                            {isFieldExpanded(booking.bookingId, 'location') && (booking.address || booking.city) && (booking.address || booking.city).length > 30 && (
                              <button
                                onClick={() => toggleFieldExpansion(booking.bookingId, 'location')}
                                className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                See Less
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Check-in:</span>
                            <p className="text-gray-900">{formatDate(booking.checkInDate)}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Check-out:</span>
                            <p className="text-gray-900">{formatDate(booking.checkOutDate)}</p>
                          </div>
                        </div>

                        {/* Price Drop Badge */}
                        {booking.isPriceDrop && (
                          <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-2 border border-green-200 rounded-lg text-sm font-medium">
                            <i className="fas fa-arrow-down mr-2"></i>
                            Price Drop: ₹{booking.newPrice} [{booking.providerName}]
                          </div>
                        )}

                        {/* Expandable Details */}
                        {isRowExpanded(booking.bookingId) && (
                          <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-sm">
                            {booking.agentId && (
                              <div>
                                <span className="font-medium text-gray-700">Agent ID:</span>
                                <p className="text-gray-900">{booking.agentId}</p>
                              </div>
                            )}
                            {booking.hotelConfirmationNo && (
                              <div>
                                <span className="font-medium text-gray-700">Hotel Confirmation:</span>
                                <p className="text-gray-900">{booking.hotelConfirmationNo}</p>
                              </div>
                            )}
                            {booking.confirmationNo && (
                              <div>
                                <span className="font-medium text-gray-700">Booking Confirmation:</span>
                                <p className="text-gray-900">{booking.confirmationNo}</p>
                              </div>
                            )}
                            <div>
                              <span className="font-medium text-gray-700">Booking Date:</span>
                              <p className="text-gray-900">{formatDate(booking.bookingDate)}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Total Price:</span>
                              <p className="text-gray-900 font-semibold text-green-600">{formatPrice(booking.totalPrice)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} results
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        <i className="fas fa-angle-double-left mr-1"></i>
                        First
                      </button>
                      
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        <i className="fas fa-chevron-left mr-1"></i>
                        Previous
                      </button>
                      
                      <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-blue-50 border border-blue-200 rounded-lg">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        Next
                        <i className="fas fa-chevron-right ml-1"></i>
                      </button>
                      
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        Last
                        <i className="fas fa-angle-double-right ml-1"></i>
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <i className="fas fa-calendar-times text-4xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm.trim() || selectedStatuses.length > 0 
                    ? 'No bookings match your filters' 
                    : 'No booking details found'
                  }
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm.trim() || selectedStatuses.length > 0 
                    ? 'Try adjusting your search criteria or filters to find what you\'re looking for.' 
                    : 'There are currently no hotel bookings in the system.'
                  }
                </p>
                {(searchTerm.trim() || selectedStatuses.length > 0) && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBookingDetails;